export interface Article {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  categorySlug: string;
  publishedAt: string;
  readTime: string;
  tags: string[];
  content: ArticleSection[];
  relatedArticles: string[];
}

export interface ArticleSection {
  type: 'heading' | 'paragraph' | 'list' | 'blockquote';
  level?: number;
  text?: string;
  items?: string[];
}

export interface Category {
  slug: string;
  name: string;
  description: string;
  icon: string;
  articleCount: number;
}

export const categories: Category[] = [
  {
    slug: 'legal-definition',
    name: '法律定义',
    description: '深入解析强奸罪的法律定义与立法沿革，了解法律条文背后的含义',
    icon: 'ri-scales-3-line',
    articleCount: 4,
  },
  {
    slug: 'elements',
    name: '构成要件',
    description: '详解强奸罪的主客观构成要件，包括行为主体、客观行为、主观故意等',
    icon: 'ri-file-search-line',
    articleCount: 4,
  },
  {
    slug: 'sentencing',
    name: '量刑标准',
    description: '全面介绍强奸罪的量刑标准、加重情节与从轻减轻情节',
    icon: 'ri-scales-line',
    articleCount: 4,
  },
  {
    slug: 'defense',
    name: '辩护策略',
    description: '专业刑事辩护策略分析，为当事人提供有效的法律救济途径',
    icon: 'ri-shield-check-line',
    articleCount: 4,
  },
  {
    slug: 'cases',
    name: '司法实践',
    description: '典型司法案例解析，了解法院裁判思路与司法实践动态',
    icon: 'ri-book-read-line',
    articleCount: 4,
  },
  {
    slug: 'regulations',
    name: '相关法规',
    description: '汇总与强奸罪相关的法律法规、司法解释及规范性文件',
    icon: 'ri-article-line',
    articleCount: 3,
  },
];

export const articles: Article[] = [
  {
    slug: 'rape-crime-legal-definition',
    title: '强奸罪的法律定义与立法沿革',
    excerpt: '强奸罪是我国刑法中严厉打击的严重暴力犯罪。本文从《刑法》第二百三十六条出发，结合历次刑法修正案，全面解析强奸罪的法律定义、保护法益及立法演变过程。',
    category: '法律定义',
    categorySlug: 'legal-definition',
    publishedAt: '2026-07-15',
    readTime: '12分钟',
    tags: ['法律定义', '刑法第236条', '立法沿革'],
    relatedArticles: ['rape-crime-object', 'rape-crime-elements-overview', 'rape-crime-sentencing-basics'],
    content: [
      { type: 'heading', level: 2, text: '一、强奸罪的法律定义' },
      { type: 'paragraph', text: '根据《中华人民共和国刑法》第二百三十六条的规定，强奸罪是指以暴力、胁迫或者其他手段，违背妇女意志，强行与其发生性关系的行为。该罪名保护的法益是妇女的性自主权，属于侵犯公民人身权利、民主权利罪中的一种严重暴力犯罪。' },
      { type: 'paragraph', text: '法律对"强奸"的定义核心在于"违背妇女意志"。这一要件是区分强奸罪与一般性行为的关键所在。在司法实践中，"违背妇女意志"的判断需要结合案件的全部客观事实进行综合认定，而不能仅凭被害人的事后陈述或被告人的单方辩解。' },
      { type: 'heading', level: 2, text: '二、立法沿革' },
      { type: 'paragraph', text: '1979年《刑法》第一百三十九条首次将强奸罪纳入刑法调整范围，规定"以暴力、胁迫或者其他手段强奸妇女的，处三年以上十年以下有期徒刑"。当时的规定较为简略，对奸淫幼女等特殊情形的规定也不够完善。' },
      { type: 'paragraph', text: '1997年《刑法》进行了全面修订，第二百三十六条对强奸罪作出了更为详尽的规定。明确了"致使被害人重伤、死亡或者造成其他严重后果的"作为加重处罚情节，并将奸淫不满十四周岁幼女的行为明确以强奸论，从重处罚。' },
      { type: 'paragraph', text: '2015年《刑法修正案（九）》对强奸罪作出了重大修改，废除了嫖宿幼女罪，将嫖宿幼女的行为直接纳入强奸罪从重处罚的范围，体现了对未成年人权益保护力度的显著提升。' },
      { type: 'paragraph', text: '2020年《刑法修正案（十一）》进一步强化了对未成年人的保护，增设了负有照护职责人员性侵罪（第二百三十六条之一），规定对已满十四周岁不满十六周岁的未成年女性负有监护、收养、看护、教育、医疗等特殊职责的人员，与该未成年女性发生性关系的，处三年以下有期徒刑；情节恶劣的，处三年以上十年以下有期徒刑。' },
      { type: 'heading', level: 2, text: '三、法律保护的法益' },
      { type: 'paragraph', text: '强奸罪所保护的核心法益是妇女的性自主权。性自主权是人格权的重要组成部分，是指自然人按照自己的意愿依法自主支配自己性利益的具体人格权。刑法通过惩处侵犯妇女性自主权的行为，保护妇女的身心健康与人格尊严。' },
      { type: 'paragraph', text: '值得注意的是，法律对性自主权的保护并非绝对。例如，婚姻关系存续期间的夫妻性行为，一般不被认定为强奸。但在特定情形下，如夫妻处于离婚诉讼期间或已经分居的，司法实践中也存在认定婚内强奸的空间，这一问题在理论上和实践中仍存在较大争议。' },
    ],
  },
  {
    slug: 'rape-crime-object',
    title: '强奸罪的犯罪对象与保护范围',
    excerpt: '明确强奸罪的犯罪对象认定标准，包括被害人主体资格、年龄界限等关键要素，以及特殊情形下的保护范围界定。',
    category: '法律定义',
    categorySlug: 'legal-definition',
    publishedAt: '2026-07-10',
    readTime: '8分钟',
    tags: ['犯罪对象', '保护范围', '被害人'],
    relatedArticles: ['rape-crime-legal-definition', 'rape-crime-elements-overview', 'rape-minor-protection'],
    content: [
      { type: 'heading', level: 2, text: '一、犯罪对象的基本认定' },
      { type: 'paragraph', text: '强奸罪的犯罪对象是女性，包括年满十四周岁的妇女和不满十四周岁的幼女。从刑法规定来看，强奸罪的直接正犯只能是男性，但女性可以成为强奸罪的教唆犯、帮助犯或间接正犯。' },
      { type: 'heading', level: 2, text: '二、幼女的特殊保护' },
      { type: 'paragraph', text: '对于不满十四周岁的幼女，法律给予了特殊的保护。根据刑法规定，奸淫不满十四周岁幼女的，以强奸论，从重处罚。此处不要求行为人使用暴力、胁迫或者其他强制手段，只要与幼女发生性关系即构成强奸罪。这是基于幼女身心发育不成熟、缺乏性同意能力的立法考量。' },
      { type: 'heading', level: 2, text: '三、特殊群体的保护' },
      { type: 'paragraph', text: '对于精神障碍女性、智力残疾女性等不能正确表达意志的特殊群体，法律同样给予特殊保护。如果行为人明知对方因精神障碍或智力残疾而不能正确表达意志，仍与其发生性关系的，应当认定为强奸罪。' },
    ],
  },
  {
    slug: 'rape-crime-elements-overview',
    title: '强奸罪构成要件全面解析',
    excerpt: '从犯罪构成四要件理论出发，系统分析强奸罪的主体要件、主观要件、客体要件和客观要件，帮助读者全面理解该罪名的法律结构。',
    category: '构成要件',
    categorySlug: 'elements',
    publishedAt: '2026-07-08',
    readTime: '15分钟',
    tags: ['构成要件', '四要件', '犯罪构成'],
    relatedArticles: ['rape-crime-objective-elements', 'rape-crime-subjective-elements', 'rape-crime-legal-definition'],
    content: [
      { type: 'heading', level: 2, text: '一、犯罪主体要件' },
      { type: 'paragraph', text: '强奸罪的犯罪主体为一般主体，即年满十四周岁、具有刑事责任能力的自然人。根据刑法规定，强奸罪的直接实行犯只能是男性。女性虽然不能单独成为强奸罪的直接正犯，但可以成为强奸罪的教唆犯、帮助犯，或者在共同犯罪中起辅助作用。' },
      { type: 'paragraph', text: '关于刑事责任年龄，已满十四周岁不满十六周岁的人犯强奸罪的，应当负刑事责任。已满十二周岁不满十四周岁的人，犯故意杀人、故意伤害致人死亡或者以特别残忍手段致人重伤造成严重残疾，情节恶劣，经最高人民检察院核准追诉的，应当负刑事责任。但强奸罪不在该特殊规定之列，因此不满十四周岁的人不能构成强奸罪的犯罪主体。' },
      { type: 'heading', level: 2, text: '二、犯罪主观要件' },
      { type: 'paragraph', text: '强奸罪的主观方面要求行为人具有犯罪故意，即明知自己的行为会发生侵犯妇女性自主权的结果，并且希望或者放任这种结果的发生。具体而言，行为人须认识到自己是在违背妇女意志的情况下实施性行为。' },
      { type: 'paragraph', text: '在司法实践中，对主观故意的认定需要结合客观行为进行判断。如果行为人确实有合理理由相信对方是自愿的，则不构成强奸罪。但"合理理由"的判断标准较为严格，一般需要从一般人的角度进行客观评价。' },
      { type: 'heading', level: 2, text: '三、犯罪客体要件' },
      { type: 'paragraph', text: '强奸罪侵犯的客体是妇女的性自主权，属于复杂客体，同时也侵犯了妇女的身心健康权和人格尊严。性自主权是妇女依法享有的按照自己的意志决定性行为的权利，是宪法和法律保护的公民基本权利。' },
      { type: 'heading', level: 2, text: '四、犯罪客观要件' },
      { type: 'paragraph', text: '强奸罪的客观方面表现为以暴力、胁迫或者其他手段，违背妇女意志，强行与妇女发生性关系的行为。这里的"暴力"是指对妇女的身体实施有形力，如殴打、捆绑、卡脖子等；"胁迫"是指对妇女实施精神强制，如以杀害、伤害、揭发隐私等相威胁；"其他手段"是指暴力、胁迫以外的使妇女不知反抗、不能反抗的手段，如利用妇女熟睡、醉酒、患病等状态。' },
    ],
  },
  {
    slug: 'rape-crime-objective-elements',
    title: '强奸罪客观行为手段详解',
    excerpt: '深入分析强奸罪中"暴力、胁迫或其他手段"的具体表现形式，包括司法实践中常见的各类行为方式及其认定标准。',
    category: '构成要件',
    categorySlug: 'elements',
    publishedAt: '2026-07-05',
    readTime: '10分钟',
    tags: ['客观行为', '暴力手段', '胁迫手段'],
    relatedArticles: ['rape-crime-elements-overview', 'rape-crime-subjective-elements', 'rape-crime-defense-strategies'],
    content: [
      { type: 'heading', level: 2, text: '一、暴力手段的认定' },
      { type: 'paragraph', text: '暴力手段是指对妇女的身体直接实施有形力，以达到压制反抗、强行发生性关系的目的。常见的暴力手段包括殴打、捆绑、卡脖子、按倒、堵嘴等。判断暴力手段是否达到"足以压制反抗"的程度，需要结合被害人的年龄、体能状况、案发环境等因素综合判断。' },
      { type: 'heading', level: 2, text: '二、胁迫手段的认定' },
      { type: 'paragraph', text: '胁迫手段是指对妇女实施精神上的强制，使其产生恐惧心理而不敢反抗。常见的胁迫手段包括以杀害、伤害相威胁，以揭发隐私、损害名誉相威胁，利用职权关系、从属关系进行胁迫等。胁迫不一定要求是明示的，也可以是暗示或利用特定情境形成的精神压力。' },
      { type: 'heading', level: 2, text: '三、其他手段的认定' },
      { type: 'paragraph', text: '"其他手段"是指暴力、胁迫以外的，使妇女不知反抗或不能反抗的手段。主要包括：利用妇女熟睡、醉酒、昏迷等状态实施奸淫；利用妇女患重病、残疾等无法反抗的状态；冒充妇女的丈夫或情人实施奸淫；以治病为名实施奸淫等。' },
    ],
  },
  {
    slug: 'rape-crime-subjective-elements',
    title: '强奸罪主观故意的司法认定',
    excerpt: '探讨强奸罪主观故意的认定标准与证明规则，包括"明知"的判断、"违背意志"的认识以及认识错误的处理原则。',
    category: '构成要件',
    categorySlug: 'elements',
    publishedAt: '2026-07-01',
    readTime: '9分钟',
    tags: ['主观故意', '明知', '认识错误'],
    relatedArticles: ['rape-crime-elements-overview', 'rape-crime-objective-elements', 'rape-crime-defense-strategies'],
    content: [
      { type: 'heading', level: 2, text: '一、主观故意的内容' },
      { type: 'paragraph', text: '强奸罪的主观故意包括两个层面的认识：第一，行为人认识到自己正在与妇女发生性关系；第二，行为人认识到该性行为是违背妇女意志的。两个认识要素缺一不可。' },
      { type: 'heading', level: 2, text: '二、"明知"的判断标准' },
      { type: 'paragraph', text: '在奸淫幼女的案件中，要求行为人"明知"对方是不满十四周岁的幼女。"明知"包括确实知道和应当知道两种情形。对于"应当知道"的判断，需要综合考虑被害人的身体发育状况、言谈举止、衣着特征、生活作息规律等多种因素。' },
      { type: 'heading', level: 2, text: '三、认识错误的处理' },
      { type: 'paragraph', text: '如果行为人确实有合理根据相信对方是自愿的，或者确实不知道且不应当知道对方是幼女的，则不构成强奸罪。但这种认识错误的认定非常严格，一般需要有充分的事实依据才能被法院采信。' },
    ],
  },
  {
    slug: 'rape-crime-sentencing-basics',
    title: '强奸罪基本量刑标准详解',
    excerpt: '系统解读强奸罪的基本量刑档次与刑罚幅度，帮助了解法律规定的量刑框架和司法实践中的参考因素。',
    category: '量刑标准',
    categorySlug: 'sentencing',
    publishedAt: '2026-06-28',
    readTime: '11分钟',
    tags: ['量刑标准', '刑罚幅度', '量刑规范'],
    relatedArticles: ['rape-crime-aggravated', 'rape-crime-mitigation', 'rape-crime-legal-definition'],
    content: [
      { type: 'heading', level: 2, text: '一、基本量刑档次' },
      { type: 'paragraph', text: '根据《刑法》第二百三十六条第一款的规定，以暴力、胁迫或者其他手段强奸妇女的，处三年以上十年以下有期徒刑。这是强奸罪的基本量刑档次。' },
      { type: 'heading', level: 2, text: '二、量刑考量因素' },
      { type: 'paragraph', text: '在三年至十年的量刑幅度内，法院需要综合考量以下因素：犯罪手段的恶劣程度；对被害人身心造成的损害程度；是否具有自首、立功、认罪认罚等从轻情节；是否取得被害人谅解；是否有犯罪前科等。' },
      { type: 'heading', level: 2, text: '三、量刑规范化改革' },
      { type: 'paragraph', text: '最高人民法院推行的量刑规范化改革对强奸罪的量刑提供了更为细致的指导。根据《关于常见犯罪的量刑指导意见》，法院在量刑时需要先确定量刑起点，再根据犯罪事实增加刑罚量确定基准刑，最后根据量刑情节调节基准刑，得出宣告刑。' },
    ],
  },
  {
    slug: 'rape-crime-aggravated',
    title: '强奸罪加重处罚情节全解析',
    excerpt: '详解《刑法》第二百三十六条规定的六种加重处罚情节，包括轮奸、奸淫幼女、公共场所当众实施等情形及对应的刑罚幅度。',
    category: '量刑标准',
    categorySlug: 'sentencing',
    publishedAt: '2026-06-25',
    readTime: '14分钟',
    tags: ['加重情节', '轮奸', '情节恶劣'],
    relatedArticles: ['rape-crime-sentencing-basics', 'rape-crime-mitigation', 'rape-minor-protection'],
    content: [
      { type: 'heading', level: 2, text: '一、强奸妇女、奸淫幼女情节恶劣的' },
      { type: 'paragraph', text: '此处的"情节恶劣"是一个概括性规定，需要从犯罪手段、犯罪后果、社会影响等多个维度进行综合判断。例如，使用特别残忍的手段、造成被害人严重身心创伤、在较长时间内多次实施强奸等，均可能被认定为情节恶劣。' },
      { type: 'heading', level: 2, text: '二、强奸妇女、奸淫幼女多人的' },
      { type: 'paragraph', text: '"多人"一般指三人及以上。如果行为人先后强奸多人，或者一次行为中强奸多人，均适用该加重情节。每一起强奸事实都需要有确实、充分的证据予以证明。' },
      { type: 'heading', level: 2, text: '三、在公共场所当众强奸妇女、奸淫幼女的' },
      { type: 'paragraph', text: '"公共场所"是指不特定多数人可以自由出入的场所，如街道、公园、商场等；"当众"是指有他人在场目睹的情形。这一情节体现了犯罪行为的极大社会危害性和对公共秩序的严重破坏。' },
      { type: 'heading', level: 2, text: '四、二人以上轮奸的' },
      { type: 'paragraph', text: '轮奸是指二人以上在同一时间、同一地点，对同一被害人先后实施强奸的行为。轮奸是强奸罪中最为严重的情形之一，起刑点为十年有期徒刑，最高可判处死刑。' },
      { type: 'heading', level: 2, text: '五、奸淫不满十周岁的幼女或者造成幼女伤害的' },
      { type: 'paragraph', text: '这是《刑法修正案（十一）》新增的加重情节，体现了对低龄幼女的特殊保护。奸淫不满十周岁幼女的，起刑点为十年有期徒刑。' },
      { type: 'heading', level: 2, text: '六、致使被害人重伤、死亡或者造成其他严重后果的' },
      { type: 'paragraph', text: '此处的"致使"要求重伤、死亡结果与强奸行为之间存在因果关系。不仅包括因暴力手段本身造成的伤亡，也包括被害人在遭受侵害后自杀等严重后果，前提是自杀与侵害行为之间存在直接的因果关系。' },
    ],
  },
  {
    slug: 'rape-crime-mitigation',
    title: '强奸罪从轻减轻情节辩护要点',
    excerpt: '梳理强奸罪中可能适用的从轻、减轻处罚情节，为辩护工作提供实务指引，包括自首、立功、认罪认罚、被害人谅解等。',
    category: '量刑标准',
    categorySlug: 'sentencing',
    publishedAt: '2026-06-20',
    readTime: '10分钟',
    tags: ['从轻情节', '减轻处罚', '量刑辩护'],
    relatedArticles: ['rape-crime-sentencing-basics', 'rape-crime-defense-strategies', 'rape-crime-aggravated'],
    content: [
      { type: 'heading', level: 2, text: '一、自首的认定与适用' },
      { type: 'paragraph', text: '犯罪以后自动投案，如实供述自己罪行的，是自首。对于自首的犯罪分子，可以从轻或者减轻处罚；犯罪较轻的，可以免除处罚。在强奸案件中，自首的认定需要特别关注投案的主动性和供述的真实性。' },
      { type: 'heading', level: 2, text: '二、立功的认定与适用' },
      { type: 'paragraph', text: '犯罪分子有揭发他人犯罪行为、查证属实的，或者提供重要线索从而得以侦破其他案件等立功表现的，可以从轻或者减轻处罚；有重大立功表现的，可以减轻或者免除处罚。' },
      { type: 'heading', level: 2, text: '三、认罪认罚从宽制度' },
      { type: 'paragraph', text: '认罪认罚从宽制度是当前刑事司法改革的重要内容。被告人自愿如实供述自己的罪行，承认指控的犯罪事实，愿意接受处罚的，可以依法从宽处理。认罪认罚的阶段越早，从宽幅度越大。' },
      { type: 'heading', level: 2, text: '四、被害人谅解' },
      { type: 'paragraph', text: '取得被害人谅解是强奸案件中重要的酌定从轻情节。被告人通过赔偿损失、赔礼道歉等方式取得被害人谅解的，可以在量刑时予以考量。但需要注意的是，被害人谅解不是法定从轻情节，法院有自由裁量权。' },
    ],
  },
  {
    slug: 'rape-crime-defense-strategies',
    title: '强奸罪有效辩护策略全攻略',
    excerpt: '从实务角度出发，全面梳理强奸罪辩护中的核心策略，包括证据辩护、程序辩护、实体辩护等多维度的辩护思路。',
    category: '辩护策略',
    categorySlug: 'defense',
    publishedAt: '2026-06-15',
    readTime: '16分钟',
    tags: ['辩护策略', '刑事辩护', '律师实务'],
    relatedArticles: ['rape-crime-evidence-defense', 'rape-crime-procedure-defense', 'rape-crime-mitigation'],
    content: [
      { type: 'heading', level: 2, text: '一、证据辩护策略' },
      { type: 'paragraph', text: '证据辩护是强奸罪辩护的核心。由于强奸案件往往具有隐蔽性，证据多呈现"一对一"的特点，因此对证据的审查和质证尤为关键。主要策略包括：质疑被害人陈述的真实性和一致性；审查生物物证的提取、保管和鉴定程序的合法性；寻找证明双方自愿的证据线索；关注案件是否存在诬告陷害的可能等。' },
      { type: 'heading', level: 2, text: '二、程序辩护策略' },
      { type: 'paragraph', text: '关注侦查行为是否合法，是否存在刑讯逼供、诱供等非法取证行为。如果存在非法证据，应当依据非法证据排除规则，申请法院予以排除。同时要注意审查被害人询问程序是否符合对性侵案件被害人的特殊保护规定。' },
      { type: 'heading', level: 2, text: '三、实体辩护策略' },
      { type: 'paragraph', text: '实体辩护主要从犯罪构成要件出发，提出合理怀疑。包括：论证性行为是否确实发生；论证是否存在暴力、胁迫或其他手段；论证行为是否违背妇女意志；论证行为人是否具有主观故意；论证犯罪情节是否达到了法定的严重程度等。' },
      { type: 'heading', level: 2, text: '四、量刑辩护策略' },
      { type: 'paragraph', text: '在证据充分、定罪可能性较大的情况下，辩护重点应转向量刑辩护。积极争取自首、立功、认罪认罚、被害人谅解等从轻减轻情节，为当事人争取最有利的量刑结果。' },
    ],
  },
  {
    slug: 'rape-crime-evidence-defense',
    title: '强奸案证据审查与质证要点',
    excerpt: '深入分析强奸案件中各类证据的审查要点与质证技巧，包括被害人陈述、被告人供述、证人证言、物证、鉴定意见等。',
    category: '辩护策略',
    categorySlug: 'defense',
    publishedAt: '2026-06-10',
    readTime: '13分钟',
    tags: ['证据审查', '质证', 'DNA鉴定'],
    relatedArticles: ['rape-crime-defense-strategies', 'rape-crime-procedure-defense', 'rape-crime-elements-overview'],
    content: [
      { type: 'heading', level: 2, text: '一、被害人陈述的审查' },
      { type: 'paragraph', text: '被害人陈述是强奸案件中最为关键的证据。辩护律师需要审查：陈述内容是否前后一致；与客观证据是否吻合；报案时间和方式是否合理；是否存在其他动机影响陈述真实性等。' },
      { type: 'heading', level: 2, text: '二、DNA等生物物证的质证' },
      { type: 'paragraph', text: 'DNA鉴定虽然在客观上具有很强的证明力，但只能证明双方发生过性接触，不能直接证明性行为是否违背妇女意志。辩护人应重点审查物证的提取、保管链是否完整，鉴定机构和鉴定人是否具备资质，鉴定方法是否科学规范。' },
      { type: 'heading', level: 2, text: '三、电子数据的审查' },
      { type: 'paragraph', text: '聊天记录、转账记录、视频录像等电子数据越来越成为强奸案件中的重要证据。辩护律师应关注电子数据的提取是否合法，内容是否完整，是否存在被篡改的可能，以及数据内容能否证明双方的交往关系和意愿状态。' },
    ],
  },
  {
    slug: 'rape-crime-procedure-defense',
    title: '强奸案程序性辩护要点分析',
    excerpt: '聚焦强奸案件程序性辩护策略，包括管辖异议、非法证据排除、被害人诉讼权利保障、强制措施变更等核心程序问题。',
    category: '辩护策略',
    categorySlug: 'defense',
    publishedAt: '2026-06-05',
    readTime: '9分钟',
    tags: ['程序辩护', '非法证据排除', '强制措施'],
    relatedArticles: ['rape-crime-defense-strategies', 'rape-crime-evidence-defense', 'rape-crime-elements-overview'],
    content: [
      { type: 'heading', level: 2, text: '一、非法证据排除规则的应用' },
      { type: 'paragraph', text: '根据《刑事诉讼法》第五十六条的规定，采用刑讯逼供等非法方法收集的犯罪嫌疑人、被告人供述和采用暴力、威胁等非法方法收集的证人证言、被害人陈述，应当予以排除。辩护人应当积极发现和收集非法取证线索，依法申请排除非法证据。' },
      { type: 'heading', level: 2, text: '二、强制措施变更申请' },
      { type: 'paragraph', text: '强奸罪的法定刑较重，实践中逮捕率较高。但辩护人可以根据案件具体情况，申请变更为取保候审或监视居住等非羁押性强制措施。申请时需要重点论证不具有社会危险性和不会妨碍诉讼进行的理由。' },
      { type: 'heading', level: 2, text: '三、被害人诉讼权利的特殊保护' },
      { type: 'paragraph', text: '性侵案件被害人享有特殊的诉讼保护，包括不公开审理、不公开个人信息、在询问时可以由女性工作人员进行等。辩护人需要了解这些特殊规定，确保诉讼程序的合法性。' },
    ],
  },
  {
    slug: 'rape-minor-protection',
    title: '未成年人性侵案件法律保护体系',
    excerpt: '全面梳理针对未成年人的性侵犯罪法律保护体系，包括刑法特别规定、未成年人保护法及最新司法解释对未成年被害人的综合保护。',
    category: '辩护策略',
    categorySlug: 'defense',
    publishedAt: '2026-05-28',
    readTime: '11分钟',
    tags: ['未成年人保护', '幼女', '特殊规定'],
    relatedArticles: ['rape-crime-legal-definition', 'rape-crime-aggravated', 'rape-crime-object'],
    content: [
      { type: 'heading', level: 2, text: '一、刑法对未成年人的特别保护' },
      { type: 'paragraph', text: '刑法对未成年人性侵犯罪构建了严密的保护网。奸淫不满十四周岁幼女的，以强奸论并从重处罚；奸淫不满十周岁幼女的，起刑点为十年有期徒刑。负有照护职责人员性侵罪将保护范围扩展到已满十四周岁不满十六周岁的未成年人。' },
      { type: 'heading', level: 2, text: '二、强制报告制度' },
      { type: 'paragraph', text: '根据《未成年人保护法》的规定，国家机关、居民委员会、村民委员会、密切接触未成年人的单位及其工作人员，在工作中发现未成年人身心健康受到侵害、疑似受到侵害或者面临其他危险情形的，应当立即向公安、民政、教育等有关部门报告。' },
      { type: 'heading', level: 2, text: '三、从业禁止制度' },
      { type: 'paragraph', text: '对于利用职业便利实施性侵未成年人犯罪，或者实施违背职业要求的特定义务的犯罪被判处刑罚的，人民法院可以根据犯罪情况和预防再犯罪的需要，禁止其自刑罚执行完毕之日或者假释之日起从事相关职业。' },
    ],
  },
  {
    slug: 'rape-case-typical-1',
    title: '典型案例解析：暴力型强奸的证据认定',
    excerpt: '通过一起典型暴力型强奸案例，分析法院在证据审查、事实认定和法律适用中的裁判思路与判断标准。',
    category: '司法实践',
    categorySlug: 'cases',
    publishedAt: '2026-05-20',
    readTime: '10分钟',
    tags: ['典型案例', '暴力型', '证据认定'],
    relatedArticles: ['rape-case-typical-2', 'rape-crime-evidence-defense', 'rape-crime-sentencing-basics'],
    content: [
      { type: 'heading', level: 2, text: '一、案情简介' },
      { type: 'paragraph', text: '被告人张某与被害人李某系同事关系。某日晚，张某以讨论工作为由将李某约至其住处，随后采取暴力手段强行与李某发生性关系。案发后，李某立即报警，公安机关在李某体内检出张某的DNA。张某辩称双方系自愿发生性关系。' },
      { type: 'heading', level: 2, text: '二、争议焦点' },
      { type: 'paragraph', text: '本案的核心争议焦点在于：被害人体内的DNA证据能否证明强奸事实的存在？在"一对一"证据对立的局面下，如何认定案件事实？' },
      { type: 'heading', level: 2, text: '三、法院裁判' },
      { type: 'paragraph', text: '法院综合审查了以下证据：被害人李某的陈述与报警时间链条一致、内容稳定；李某身上多处淤青与暴力行为吻合；案发现场勘验笔录显示有明显打斗痕迹；多名证人证实案发后李某情绪异常。综合全案证据，法院认定张某构成强奸罪，判处有期徒刑四年六个月。' },
    ],
  },
  {
    slug: 'rape-case-typical-2',
    title: '典型案例解析：醉酒状态强奸的认定',
    excerpt: '分析利用被害人醉酒状态实施性侵案件的裁判规则，探讨"不知反抗、不能反抗"的司法认定标准。',
    category: '司法实践',
    categorySlug: 'cases',
    publishedAt: '2026-05-15',
    readTime: '9分钟',
    tags: ['典型案例', '醉酒', '不能反抗'],
    relatedArticles: ['rape-case-typical-1', 'rape-crime-objective-elements', 'rape-crime-elements-overview'],
    content: [
      { type: 'heading', level: 2, text: '一、案情简介' },
      { type: 'paragraph', text: '被告人王某与朋友聚会后，趁被害人赵某醉酒意识不清醒之际，将其带至酒店实施性侵。次日赵某醒后报警。王某辩称赵某当时意识清醒且表示同意。' },
      { type: 'heading', level: 2, text: '二、裁判要旨' },
      { type: 'paragraph', text: '法院认为，利用被害人醉酒后不知反抗、不能反抗的状态实施性侵，属于"其他手段"强奸。判断被害人是否处于"不能反抗"状态，应结合血液酒精浓度检测、在场证人证言、监控录像显示的行走姿态和行为表现等客观证据综合认定，不能仅凭被告人的单方辩解。' },
    ],
  },
  {
    slug: 'rape-case-typical-3',
    title: '典型案例解析：婚内强奸的司法认定',
    excerpt: '探讨婚内强奸这一特殊情形的法律适用，分析在婚姻关系存续期间认定强奸罪的司法实践与裁判趋势。',
    category: '司法实践',
    categorySlug: 'cases',
    publishedAt: '2026-05-10',
    readTime: '10分钟',
    tags: ['典型案例', '婚内强奸', '特殊情形'],
    relatedArticles: ['rape-case-typical-1', 'rape-case-typical-2', 'rape-crime-legal-definition'],
    content: [
      { type: 'heading', level: 2, text: '一、婚内强奸的法律争议' },
      { type: 'paragraph', text: '婚内强奸是指在合法婚姻关系存续期间，丈夫违背妻子意志，强行与之发生性关系的行为。由于婚姻关系的特殊性，婚内强奸是否构成强奸罪在理论和实务中存在较大争议。' },
      { type: 'heading', level: 2, text: '二、司法实践中的认定趋势' },
      { type: 'paragraph', text: '近年来，司法实践中出现了认定婚内强奸构成犯罪的案例。特别是在以下情形中法院倾向于认定为犯罪：双方处于离婚诉讼期间；双方已经长期分居；丈夫的行为手段特别恶劣、造成严重后果等。这表明司法实践对婚内强奸的态度正在从绝对豁免向有条件入罪转变。' },
    ],
  },
  {
    slug: 'rape-case-typical-4',
    title: '典型案例解析：熟人强奸案件的审理难点',
    excerpt: '分析熟人之间强奸案件的审理特点与难点，包括证据认定、主观故意判断及被害人陈述的可信度评估等核心问题。',
    category: '司法实践',
    categorySlug: 'cases',
    publishedAt: '2026-05-05',
    readTime: '9分钟',
    tags: ['典型案例', '熟人强奸', '审理难点'],
    relatedArticles: ['rape-case-typical-1', 'rape-case-typical-3', 'rape-crime-subjective-elements'],
    content: [
      { type: 'heading', level: 2, text: '一、熟人强奸案件的特点' },
      { type: 'paragraph', text: '统计数据显示，强奸案件中熟人作案占比较高。熟人强奸由于双方存在特定的社会关系，往往具有隐蔽性强、证据少、争议大等特点，给案件审理带来了特殊困难。' },
      { type: 'heading', level: 2, text: '二、审理中的难点与对策' },
      { type: 'paragraph', text: '熟人强奸案件的核心难点在于如何区分"半推半就"与"违背意志"。法院在审理此类案件时，需要综合考察双方的关系性质、交往历史、案发前后的行为表现、报警的时间与方式等因素，审慎作出判断。' },
    ],
  },
  {
    slug: 'criminal-law-236',
    title: '《刑法》第二百三十六条全文与解读',
    excerpt: '提供《中华人民共和国刑法》第二百三十六条全文及逐款专业解读，帮助理解立法原意和司法适用要点。',
    category: '相关法规',
    categorySlug: 'regulations',
    publishedAt: '2026-04-28',
    readTime: '12分钟',
    tags: ['刑法', '第236条', '法条解读'],
    relatedArticles: ['rape-crime-legal-definition', 'rape-crime-sentencing-basics', 'judicial-interpretation-rape'],
    content: [
      { type: 'heading', level: 2, text: '《刑法》第二百三十六条全文' },
      { type: 'paragraph', text: '第二百三十六条　以暴力、胁迫或者其他手段强奸妇女的，处三年以上十年以下有期徒刑。' },
      { type: 'paragraph', text: '奸淫不满十四周岁的幼女的，以强奸论，从重处罚。' },
      { type: 'paragraph', text: '强奸妇女、奸淫幼女，有下列情形之一的，处十年以上有期徒刑、无期徒刑或者死刑：' },
      { type: 'paragraph', text: '（一）强奸妇女、奸淫幼女情节恶劣的；' },
      { type: 'paragraph', text: '（二）强奸妇女、奸淫幼女多人的；' },
      { type: 'paragraph', text: '（三）在公共场所当众强奸妇女、奸淫幼女的；' },
      { type: 'paragraph', text: '（四）二人以上轮奸的；' },
      { type: 'paragraph', text: '（五）奸淫不满十周岁的幼女或者造成幼女伤害的；' },
      { type: 'paragraph', text: '（六）致使被害人重伤、死亡或者造成其他严重后果的。' },
      { type: 'heading', level: 2, text: '条文解读要点' },
      { type: 'paragraph', text: '本条是对强奸罪的基本规定。第一款规定了强奸罪的基本罪状和量刑幅度，法定刑为三年以上十年以下有期徒刑。第二款是对奸淫幼女的特别规定，体现了法律对幼女的特殊保护。第三款规定了六种加重处罚情节，法定最高刑为死刑，体现了国家对强奸犯罪从严惩处的立法精神。' },
    ],
  },
  {
    slug: 'judicial-interpretation-rape',
    title: '强奸罪相关司法解释汇编',
    excerpt: '汇总最高人民法院、最高人民检察院发布的与强奸罪相关的司法解释及指导意见，提供权威的法律适用参考。',
    category: '相关法规',
    categorySlug: 'regulations',
    publishedAt: '2026-04-20',
    readTime: '13分钟',
    tags: ['司法解释', '指导意见', '法律适用'],
    relatedArticles: ['criminal-law-236', 'rape-crime-legal-definition', 'rape-minor-protection'],
    content: [
      { type: 'heading', level: 2, text: '一、《关于依法惩治性侵害未成年人犯罪的意见》' },
      { type: 'paragraph', text: '2013年，最高人民法院、最高人民检察院、公安部、司法部联合发布该意见，对办理性侵害未成年人犯罪案件的基本原则、证据审查、法律适用等问题做出了全面系统的规定。' },
      { type: 'heading', level: 2, text: '二、《关于办理强奸、猥亵未成年人刑事案件适用法律若干问题的解释》' },
      { type: 'paragraph', text: '2023年发布的最新司法解释，进一步明确了强奸、猥亵未成年人犯罪的定罪量刑标准，强化了对未成年人的综合司法保护。' },
      { type: 'heading', level: 2, text: '三、量刑指导意见中的相关规定' },
      { type: 'paragraph', text: '最高人民法院《关于常见犯罪的量刑指导意见》对强奸罪的量刑起点、基准刑确定方法以及各类量刑情节的调节比例做出了具体规定，为法院量刑提供了规范化参考。' },
    ],
  },
  {
    slug: 'related-laws-rape',
    title: '强奸罪相关法律法规体系梳理',
    excerpt: '系统梳理与强奸罪相关的完整法律法规体系，包括《刑法》《刑事诉讼法》《未成年人保护法》等，构建全面的法律知识框架。',
    category: '相关法规',
    categorySlug: 'regulations',
    publishedAt: '2026-04-15',
    readTime: '10分钟',
    tags: ['法律体系', '刑事诉讼法', '未成年人保护法'],
    relatedArticles: ['criminal-law-236', 'judicial-interpretation-rape', 'rape-crime-legal-definition'],
    content: [
      { type: 'heading', level: 2, text: '一、《刑法》中的相关规定' },
      { type: 'paragraph', text: '除第二百三十六条外，《刑法》中与性侵犯罪相关的条文还包括：第二百三十六条之一（负有照护职责人员性侵罪）、第二百三十七条（强制猥亵、侮辱罪）等，这些条文共同构成了刑法对性侵犯罪的完整规制体系。' },
      { type: 'heading', level: 2, text: '二、《刑事诉讼法》中的特殊规定' },
      { type: 'paragraph', text: '考虑到性侵犯罪被害人的特殊保护需求，《刑事诉讼法》规定此类案件一般不公开审理，被害人的个人信息应当予以保护，询问时应当有女性工作人员在场等。' },
      { type: 'heading', level: 2, text: '三、《未成年人保护法》的相关规定' },
      { type: 'paragraph', text: '2020年修订的《未成年人保护法》增设了强制报告制度和从业禁止制度，为预防和惩治性侵害未成年人犯罪提供了更为全面的法律保障。' },
    ],
  },
  {
    slug: 'rape-crime-procedure-guide',
    title: '强奸案件刑事程序全程指引',
    excerpt: '从立案、侦查、审查起诉到审判各阶段，全面梳理强奸案件的刑事程序流程，帮助当事人了解诉讼权利和程序节点。',
    category: '法律定义',
    categorySlug: 'legal-definition',
    publishedAt: '2026-07-20',
    readTime: '14分钟',
    tags: ['刑事程序', '诉讼流程', '权利保障'],
    relatedArticles: ['rape-crime-legal-definition', 'rape-crime-defense-strategies', 'rape-crime-procedure-defense'],
    content: [
      { type: 'heading', level: 2, text: '一、立案阶段' },
      { type: 'paragraph', text: '强奸案件属于公诉案件，由公安机关立案侦查。被害人报案后，公安机关应当立即受理并进行审查。符合立案条件的，应当在规定期限内立案；不符合立案条件的，应当制作不予立案通知书。被害人对不立案决定不服的，可以申请复议或向人民检察院申请立案监督。' },
      { type: 'heading', level: 2, text: '二、侦查阶段' },
      { type: 'paragraph', text: '侦查阶段是证据收集的关键时期。公安机关应当全面收集、固定证据，包括现场勘验、人身检查、DNA鉴定、询问被害人及证人、讯问犯罪嫌疑人等。辩护律师在侦查阶段可以会见犯罪嫌疑人，提供法律帮助。' },
      { type: 'heading', level: 2, text: '三、审查起诉阶段' },
      { type: 'paragraph', text: '案件侦查终结后移送人民检察院审查起诉。辩护律师可以查阅、摘抄、复制案卷材料，向检察院提出辩护意见。检察院审查后认为证据不足的，可以退回补充侦查；认为不构成犯罪的，应当作出不起诉决定。' },
      { type: 'heading', level: 2, text: '四、审判阶段' },
      { type: 'paragraph', text: '强奸案件涉及个人隐私，依法不公开审理。庭审中，辩护律师应当围绕证据的合法性、真实性和关联性进行质证，就事实认定和法律适用提出辩护意见。一审判决后，被告人和检察院均有权在法定期限内提起上诉。' },
    ],
  },
  {
    slug: 'rape-crime-victim-rights',
    title: '强奸案被害人法律维权指南',
    excerpt: '为强奸案被害人提供全面的法律维权指引，包括报案流程、证据保全、诉讼权利、心理援助及社会救助资源等内容。',
    category: '法律定义',
    categorySlug: 'legal-definition',
    publishedAt: '2026-07-18',
    readTime: '11分钟',
    tags: ['被害人', '维权指南', '法律援助'],
    relatedArticles: ['rape-crime-legal-definition', 'rape-crime-procedure-guide', 'rape-case-typical-1'],
    content: [
      { type: 'heading', level: 2, text: '一、案发后的紧急处理' },
      { type: 'paragraph', text: '遭遇侵害后，被害人应当第一时间拨打110报警。同时，注意保留相关证据：不要洗澡、不要更换衣物、不要清理现场；保存所有可能有嫌疑人生物痕迹的物品；如有通讯记录，及时截图保存。前往医院进行检查和治疗，由医生进行身体检查并出具伤情证明。' },
      { type: 'heading', level: 2, text: '二、刑事诉讼中的权利' },
      { type: 'paragraph', text: '被害人作为刑事诉讼当事人，享有以下重要权利：委托诉讼代理人参与诉讼；申请回避；提起附带民事诉讼要求赔偿；对不立案决定申请复议和检察监督；对一审判决不服的，可以请求检察院提起抗诉。' },
      { type: 'heading', level: 2, text: '三、心理援助与社会支持' },
      { type: 'paragraph', text: '遭受性侵害后，被害人往往面临严重的心理创伤。各地妇联、法律援助中心、专业心理咨询机构都可以提供免费的咨询和援助服务。被害人不必独自面对，应当积极寻求社会支持。' },
    ],
  },
  {
    slug: 'rape-crime-entrapment',
    title: '强奸案中"陷害"与"诬告"的辩护策略',
    excerpt: '针对强奸案件中可能存在的诬告陷害情形，提供专业的证据收集思路、交叉询问技巧及无罪辩护策略分析。',
    category: '量刑标准',
    categorySlug: 'sentencing',
    publishedAt: '2026-06-30',
    readTime: '11分钟',
    tags: ['诬告陷害', '无罪辩护', '交叉询问'],
    relatedArticles: ['rape-crime-defense-strategies', 'rape-crime-evidence-defense', 'rape-crime-mitigation'],
    content: [
      { type: 'heading', level: 2, text: '一、诬告陷害情形的识别' },
      { type: 'paragraph', text: '在强奸案件中，虽然绝大多数报案是真实的，但实践中也存在诬告陷害的情形。常见的动机包括：掩盖自愿性行为被发现后的尴尬、情感纠纷引发的报复、经济纠纷、家庭压力等。辩护律师应当保持敏锐，留意案件中是否有诬告的线索。' },
      { type: 'heading', level: 2, text: '二、证据收集与质证策略' },
      { type: 'paragraph', text: '在怀疑存在诬告可能的情况下，辩护律师应重点收集：双方的通讯记录和社交互动信息；案发前后的视频监控资料；能够证明双方关系性质的其他证据；能够质疑被害人陈述真实性的矛盾点和不合逻辑之处。' },
    ],
  },
];

export const teamMembers = [
  {
    name: '王明远',
    title: '主任律师 / 高级合伙人',
    description: '从事刑事辩护二十余年，曾办理多起在全国具有重大影响力的刑事案件。擅长性侵犯罪、经济犯罪等复杂刑事案件的辩护，多次获得"全国优秀律师"称号。',
    imageSeq: 'lawyer-wang',
  },
  {
    name: '陈思远',
    title: '合伙人律师',
    description: '法学博士，原某省高级人民法院刑事审判庭法官，具有丰富的审判经验和深厚的法学理论功底。专注刑事辩护领域十五年，尤其擅长证据辩护和程序辩护。',
    imageSeq: 'lawyer-chen',
  },
  {
    name: '林雪峰',
    title: '资深律师',
    description: '硕士研究生学历，曾在某市人民检察院工作八年。转任律师后专注刑事辩护，办理强奸、杀人等暴力犯罪案件数百件，具有丰富的实务经验和出色的庭审表现。',
    imageSeq: 'lawyer-lin',
  },
  {
    name: '赵雅文',
    title: '律师 / 心理咨询师',
    description: '具有法学和心理学双学位，国家二级心理咨询师。专注为性侵案件当事人提供专业法律服务和心理支持，擅长处理涉及未成年人、女性当事人的敏感案件。',
    imageSeq: 'lawyer-zhao',
  },
];

export const honors = [
  '全国优秀律师事务所（2023年度）',
  '刑事辩护专业十佳律师事务所',
  '司法部法律援助先进集体',
  '省级文明律师事务所',
  '消费者信得过法律服务机构',
  '中国律师行业杰出贡献奖',
];

export const faqs = [
  {
    question: '强奸罪的追诉时效是多长？',
    answer: '根据《刑法》第八十七条的规定，强奸罪基本犯的法定最高刑为十年有期徒刑，追诉时效为十五年；具有加重情节的强奸罪法定最高刑为死刑，追诉时效为二十年。如果二十年以后认为必须追诉的，须报请最高人民检察院核准。',
  },
  {
    question: '被强奸后应该立即做什么？',
    answer: '遭遇侵害后应当立即报警（拨打110），同时注意保留证据：不要洗澡、不要更换衣物、不要清理现场；尽快前往医院进行身体检查和伤情鉴定；保存通讯记录等证据；寻求法律援助和心理支持。',
  },
  {
    question: '强奸案可以私了吗？',
    answer: '强奸罪属于公诉案件，本质上是国家对犯罪行为的追诉，不能由当事人"私了"解决。即使被害人表示谅解、不追究，也不影响司法机关依法追究行为人的刑事责任。但取得被害人谅解可以作为量刑时从轻处罚的情节。',
  },
  {
    question: '强奸罪可以取保候审吗？',
    answer: '强奸罪由于法定刑较重，取保候审的难度较大，但并非完全不可行。如果案件存在证据不足、被告人没有社会危险性、可能判处轻刑等情形，辩护律师可以依法申请取保候审。在侦查阶段的"黄金37天"内积极申请效果更佳。',
  },
  {
    question: '醉酒后发生性关系是否一定构成强奸？',
    answer: '利用被害人醉酒后"不知反抗、不能反抗"的状态实施性侵，属于"其他手段"强奸。但要结合血液酒精浓度、监控录像、证人证言等客观证据综合判断被害人当时是否确实处于不能反抗的状态。如果双方均处于自愿醉酒状态，一般不认定为强奸。',
  },
  {
    question: '强奸案需要请律师吗？',
    answer: '对于被害人而言，委托律师可以帮助了解自身权利、协助收集证据、参与诉讼程序，更好地维护自身合法权益。对于犯罪嫌疑人/被告人而言，委托专业的刑事辩护律师尤为重要，律师可以从证据、程序、实体、量刑等多角度进行专业辩护，最大限度地维护当事人的合法权利。',
  },
];